import { setUserId as fbSetUserId } from 'firebase/analytics'
import { onAuthStateChanged, getAuth } from 'firebase/auth'
import { ref, getDatabase, onValue } from 'firebase/database'
import { useMemo, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Outlet, useLocation, useMatches } from 'react-router-dom'
import { analytics } from '../firebase/configs'
import { GENERAL_API, getMerchById } from '../firebase/general-apis'
import { batchRequests } from '../firebase/helpers'
import { Product } from '../firebase/types'
import useGetScreenType from '../hooks/useGetScreenType'
import { useUserListener } from '../hooks/useUserRegistrationListener'
import { resetEccOnboarding } from '../redux/eccOnboardingSlice'
import { resetSchoolOnboarding } from '../redux/schoolOnboardingSlice'
import { RootState } from '../redux/store'
import { setScreenType, setFbStatus } from '../redux/uiSlice'
import { captureMessage, setUser as sentrySetUser } from '@sentry/react'
import { setUserLogout, deleteFromShopCart, setAuth } from '../redux/userSlice'
import ScrollToTopListenerComponent from '../components/ScrollToTopListenerComponent'
import { Helmet } from 'react-helmet-async'
import { JSONEqual } from '../redux/helpers'
import { logger } from '../helpers/logger'

const TRACKING_ID = 'G-LK9Q25DC1X'
let reactGAPromise: Promise<(typeof import('react-ga4'))['default']> | null =
    null

const getReactGA = async () => {
    if (process.env.REACT_APP_TARGET_ENV !== 'production') {
        return null
    }

    if (!reactGAPromise) {
        reactGAPromise = import('react-ga4').then(({ default: ReactGA }) => {
            ReactGA.initialize(TRACKING_ID)
            return ReactGA
        })
    }

    return reactGAPromise
}

const FB_CONNECTION_TIMEOUT_MS = 5000

const reportFirebaseConnectionEvent = (
    status: 'connected' | 'disconnected',
    source: 'initial-timeout' | 'connection-change' | 'recovered'
) => {
    const context = {
        route: window.location.pathname,
        source,
        online: navigator.onLine,
    }

    logger.warn(`Firebase database ${status}`, context)
    captureMessage(`Firebase database ${status}`, {
        level: status === 'disconnected' ? 'warning' : 'info',
        tags: {
            feature: 'Firebase connection',
            eventType: `firebase_connection_${status}`,
        },
        extra: context,
    })
}

const UserLayout = () => {
    logger.debug('RENDER: UserLayout')

    return (
        <>
            <ScreenManager />
            <LocationManager />
            <ScrollToTopListenerComponent />
            <ShopCartManager />
            <UserListenerManager />
            <DBConnectionCheck />
            <Outlet />
        </>
    )
}

const DBConnectionCheck = () => {
    const dispatch = useDispatch()
    const hasConnectedRef = useRef(false)
    const lastReportedStatusRef = useRef<boolean | undefined>(undefined)

    const reportStatus = (
        isConnected: boolean,
        source: 'initial-timeout' | 'connection-change' | 'recovered'
    ) => {
        if (lastReportedStatusRef.current === isConnected) return

        lastReportedStatusRef.current = isConnected
        reportFirebaseConnectionEvent(
            isConnected ? 'connected' : 'disconnected',
            source
        )
    }

    // Check for Firebase DB Connection Check
    useEffect(() => {
        const connRef = ref(getDatabase(), '.info/connected')
        const timeoutId = setTimeout(() => {
            if (!hasConnectedRef.current) {
                dispatch(setFbStatus(false))
                reportStatus(false, 'initial-timeout')
            }
        }, FB_CONNECTION_TIMEOUT_MS)

        const unsub = onValue(connRef, (snap) => {
            const isConnected = Boolean(snap.val())

            if (isConnected) {
                // const wasDisconnected = lastReportedStatusRef.current === false
                hasConnectedRef.current = true
                clearTimeout(timeoutId)
                dispatch(setFbStatus(true))

                if (lastReportedStatusRef.current === undefined) {
                    lastReportedStatusRef.current = true
                    return
                }

                // reportStatus(
                //     true,
                //     wasDisconnected ? 'recovered' : 'connection-change'
                // )
                return
            }

            if (hasConnectedRef.current) {
                dispatch(setFbStatus(false))
                // reportStatus(false, 'connection-change')
            }
        })

        return () => {
            clearTimeout(timeoutId)
            unsub()
        }
    }, [dispatch])

    return null
}

const ScreenManager = () => {
    const dispatch = useDispatch()
    const screenType = useGetScreenType()
    useEffect(() => {
        dispatch(setScreenType(screenType))
    }, [screenType, dispatch])
    return null
}

const LocationManager = () => {
    const location = useLocation()

    // NOTE: Move user timestamp update to login action
    // const userId = useSelector(
    //     (state: RootState) => state.user.auth.status?.uid
    // )
    // Update user last verified on location change
    // const prevPath = useRef<string>('')
    // useEffect(() => {
    //     const pathGroup = location.pathname.split('/')[1]
    //     if (userId && prevPath.current !== pathGroup) {
    //         console.log(`Update usertimestamp`)
    //         prevPath.current = pathGroup
    //         updateUserTimestamp(userId)
    //     }
    // }, [location.pathname, userId])

    // User behaviour analytics
    useEffect(() => {
        void getReactGA().then((ReactGA) => {
            if (!ReactGA) return

            ReactGA.send({
                hitType: 'pageview',
                page: location.pathname + location.search,
            })
        })
    }, [location.pathname, location.search])

    // useEffect(() => {
    //     console.log(location.pathname)
    // }, [location.pathname])

    const matches = useMatches()

    const meta = useMemo(() => {
        const temp = [...matches]
            .reverse()
            .map((match) => {
                const routeMeta = (match.handle as any)?.meta
                return typeof routeMeta === 'function'
                    ? routeMeta({ params: match.params })
                    : routeMeta
            })
            .find(Boolean)

        return {
            title: temp?.title || 'Spark Relief Teachers',
            description:
                temp?.description ||
                'Connecting Casual Relief Teachers and Schools throughout Victoria',
        }
    }, [matches])

    return (
        <Helmet>
            <meta charSet="utf-8" />
            <title>{meta.title}</title>
            <meta name="description" content={meta.description} />
            {/* Open Graph tags  */}
            <meta property="og:title" content="Spark Relief Teachers" />
            <meta
                property="og:description"
                content="Connecting Casual Relief Teachers
        and Schools throughout Victoria."
            />
            <meta property="og:image" content="%PUBLIC_URL%/logo192.png" />
            <meta property="og:image:width" content="1200" />
            <meta property="og:image:height" content="630" />
            <meta property="og:type" content="website" />
            <meta
                property="og:url"
                content={'https://www.sparkreliefteachers.com/'}
            />
        </Helmet>
    )
}

const UserListenerManager = () => {
    const dispatch = useDispatch()
    const userDelete = useSelector(
        (state: RootState) => state.userReg.userDelete
    )

    // User node listener
    useUserListener()

    // Firebase auth listener
    useEffect(() => {
        logger.debug('ATTACH-LISTENER: auth')
        const updateSignInStatus = () =>
            onAuthStateChanged(getAuth(), async (user) => {
                // Set Auth Object
                dispatch(setAuth(user !== null ? user.toJSON() : null))

                if (user) {
                    fbSetUserId(analytics, user.uid)
                    // Set Sentry user for crash analytics
                    sentrySetUser({
                        id: user.uid,
                        email: user.email || undefined,
                        name: user.displayName,
                    })

                    // Update user timestamp here - fire and forget
                    setTimeout(
                        () => GENERAL_API.updateUserTimestamp(user.uid),
                        300
                    )
                } else {
                    if (userDelete !== undefined && userDelete === false) {
                        // Handle user delete scenario
                        dispatch(setUserLogout())
                    }
                    dispatch(resetSchoolOnboarding())
                    dispatch(resetEccOnboarding())
                }
            })
        const unsubscribeAuthStateChange = updateSignInStatus()
        return () => {
            logger.debug('DETACH-LISTENER: auth')
            // Reset auth
            dispatch(setAuth(undefined))
            unsubscribeAuthStateChange()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userDelete])
    return null
}

const ShopCartManager = () => {
    const dispatch = useDispatch()
    const shopCart = useSelector(
        (state: RootState) => state.user.shopCart,
        JSONEqual
    )
    const fbIdsInStorage = useMemo(() => {
        const storageFbIds = shopCart.map((cartItem) => cartItem.fbId)
        return Array.from(new Set(storageFbIds))
    }, [shopCart])

    // Shop cart?
    useEffect(() => {
        // Delete products in shopcart storage that are already deleted in website
        const validateShopcartStorage = async () => {
            const res = await batchRequests<Product | undefined>({
                asyncFuncs: fbIdsInStorage.map(
                    (fbId) => () => getMerchById(fbId)
                ),
                count: 20,
                errorMsg: 'validate local storage of shop cart items.',
            })
            if (res.code === 200) {
                const merchandiseExists = (
                    (res.value as (Product | undefined)[]).filter(
                        (r) => r
                    ) as Product[]
                ).reduce<{
                    [fbId: string]: boolean
                }>((acc, cur) => {
                    acc[cur.id] = true
                    return acc
                }, {})
                shopCart.forEach((item) => {
                    if (!merchandiseExists[item.fbId]) {
                        dispatch(deleteFromShopCart(item))
                    }
                })
            }
        }
        validateShopcartStorage()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fbIdsInStorage, shopCart])

    return null
}

export default UserLayout
